'use client'
import debounce from 'debounce-promise'
import React, { ReactNode, SyntheticEvent, useEffect, useState } from 'react'
import Select, { StylesConfig } from 'react-select'
import AsyncSelect from 'react-select/async'
import { SelectComponents } from 'react-select/dist/declarations/src/components'
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters'
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager'
import useInputWrapper from '../../../hooks/useInputWrapper'
import { TextTypes } from '../../../types'
import { Spinner, Text } from '../../atoms'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    MultiValueComponent,
    SingleValueMultiLineComponent,
    SingleValueTagComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'

import { useId } from 'react'
import { SelectInputProps, SelectOption, GroupedSelectOption } from '../../../types/SelectInput'

/**
 * @deprecated
 */
export const SelectInput = ({
    options = [],
    asyncQuery,
    asyncOptions,
    isMulti = false,
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    isAsync = false,
    isLoading,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    defaultInputValue = undefined,
    noOptionsMessage = 'No results',
    isInline = false,
    className = '',
    placeholder,
    autoFocus = false,
    asyncMinimumCharacters = 3,
    isClearable = false,
    ...props
}: SelectInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)
    const selectId = id ?? useId()

    const [showAsyncResults, setShowAsyncResults] = useState<boolean>(false)

    const components: Partial<SelectComponents<SelectOption, boolean, GroupedSelectOption>> = {
        MultiValue: MultiValueComponent,
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    if (showAsTag) components.SingleValue = SingleValueTagComponent
    if (showMultiLine) components.SingleValue = SingleValueMultiLineComponent

    const customStyles: StylesConfig<SelectOption, boolean, GroupedSelectOption> = {
        valueContainer: (provided, _) => ({
            ...provided,
            ...(showMultiLine && { display: 'flex', flexWrap: 'nowrap' }),
            cursor: 'pointer',
        }),
        placeholder: (provided, state) => ({
            ...provided,
            ...(state.isFocused && isSearchable && { display: 'none' }),
        }),
        menu: (provided) => ({
            ...provided,
            marginTop: '4px',
            marginBottom: '4px',
            zIndex: '100',
            borderRadius: 0,
        }),
    }

    const selectClassNames = `select-input ${isMulti ? 'select-input--multi' : 'select-input--single'} ${
        isInline ? 'select-input--inline' : 'select-input--block'
    } ${isSearchable ? 'select-input--searchable' : 'select-input--not-searchable'} ${
        readOnly ? 'select-input--readonly' : ''
    } ${showAsTag && !isMulti ? 'select-input--show-as-tag' : ''}`

    /** Loads options for searchable async options */
    const loadAsyncQueryOptions = (inputValue: string): Promise<GroupedSelectOption[]> => {
        const isValidSearch: boolean = inputValue?.length >= asyncMinimumCharacters
        const emptyResults = new Promise<GroupedSelectOption[]>((resolve) => resolve([]))

        if (!asyncQuery) return emptyResults
        if (!isSearchable) return asyncQuery('') // gets all results if not searchable

        return isValidSearch ? asyncQuery(inputValue) : emptyResults
    }

    const debouncedLoadOptions: (inputValue: string) => Promise<SelectOption[] | GroupedSelectOption[]> = debounce(
        loadAsyncQueryOptions,
        250,
    )

    const loadAsyncOptions = (): Promise<SelectOption[]> => {
        const emptyResults = new Promise<SelectOption[]>((resolve) => resolve([]))
        return asyncOptions ? asyncOptions() : emptyResults
    }

    const LoadingSpinner = (): ReactNode => (
        <div className="flex justify-center">
            <Spinner />
        </div>
    )

    const formatGroupLabel = (group: GroupedSelectOption): ReactNode => (
        <div className="border-b border-shade-200 pb-1 -mx-3">
            <Text type={TextTypes.SMALL_TEXT} className="normal-case text-xs px-3">
                {group.label}
            </Text>
        </div>
    )

    const handleAsyncInput = (input: string): void => setShowAsyncResults(input.length >= asyncMinimumCharacters)

    const getFilterOption = (option: FilterOptionOption<SelectOption>, input: string): boolean => {
        if (showMultiLine) {
            return input ? `${option.label} ${option.data.footer}`.toLowerCase().includes(input.toLowerCase()) : true
        }

        return input ? option.label?.toLowerCase().includes(input.toLowerCase()) : true
    }

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        className: selectClassNames,
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        isMulti,
        closeMenuOnSelect: !isMulti,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        components,
        styles: customStyles,
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        loadingMessage: LoadingSpinner,
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(!isAsync && { filterOption: getFilterOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
        onFocus: onFocusHandler,
        isOptionDisabled: (option: SelectOption) => 'disabled' in option && !!option.disabled,
    }

    return (
        <div
            className={`border items-center ${isInline ? 'inline-flex' : 'flex'}  ${classes} ${className}`}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            {isAsync ? (
                <AsyncSelect
                    {...selectProps}
                    isClearable={true}
                    cacheOptions
                    loadOptions={isSearchable ? debouncedLoadOptions : loadAsyncOptions}
                    formatGroupLabel={formatGroupLabel}
                    onInputChange={handleAsyncInput}
                    onChange={(option) => onChange && onChange(option as SelectOption & SelectOption[])}
                    {...(!isSearchable && { defaultOptions: true })}
                    {...(isSearchable && {
                        menuIsOpen: showAsyncResults,
                        openMenuOnClick: false,
                    })}
                    defaultInputValue={defaultInputValue}
                    value={
                        isMulti
                            ? options.filter((option) =>
                                  Array.isArray(value) ? value?.includes(option.value) : value === option.value,
                              )
                            : selectProps.value
                    }
                />
            ) : (
                <Select
                    {...selectProps}
                    defaultValue={defaultValue}
                    isClearable={(!readOnly && isMulti) || isClearable}
                    isLoading={isLoading}
                    options={options}
                    value={options.filter((option) =>
                        isMulti && Array.isArray(value) ? value?.includes(option.value) : value === option.value,
                    )}
                    onChange={(option) => onChange && onChange(option as SelectOption & SelectOption[])}
                    openMenuOnClick={!readOnly}
                />
            )}
        </div>
    )
}
